import React, { useEffect, useState } from "react";
import axios from "axios";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import BackOfficeMenu from "../components/BackOfficeMenu";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import { usePermissions } from "../../utils/usePermissions";
import "./DashboardVehicule.css";

const API_URL = import.meta.env.VITE_API_URL;

const COLORS = {
    primaryBlue: '#1e3a5f',
    primaryYellow: '#ffd700',
    accentOrange: '#ff8c00',
    green: '#00C49F',
    red: '#ff4d4d',
    lightBlue: '#e8f4fd'
};

const formatLocalDateInput = (date = new Date()) => {
    const offset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - offset).toISOString().split('T')[0];
};

const getDateRange = (mode, referenceDate) => {
    const baseDate = new Date(`${referenceDate}T00:00:00`);
    const start = new Date(baseDate);
    const end = new Date(baseDate);

    if (mode === 'week') {
        const day = baseDate.getDay();
        const diffToMonday = (day + 6) % 7;
        start.setDate(baseDate.getDate() - diffToMonday);
        end.setDate(start.getDate() + 6);
    } else if (mode === 'month') {
        start.setDate(1);
        end.setMonth(baseDate.getMonth() + 1, 0);
    } else if (mode === 'year') {
        start.setMonth(0, 1);
        end.setMonth(11, 31);
    }

    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);

    return {
        startDate: start.toISOString(),
        endDate: end.toISOString()
    };
};

const mergeDateRange = (mode, startDate, endDate) => {
    if (startDate && endDate) {
        return {
            startDate: new Date(`${startDate}T00:00:00`).toISOString(),
            endDate: new Date(`${endDate}T23:59:59.999`).toISOString()
        };
    }

    const referenceDate = startDate || endDate || formatLocalDateInput();
    return getDateRange(mode, referenceDate);
};

const getPresetRangeForMode = (mode) => getDateRange(mode, formatLocalDateInput());

const DashboardVehicule = ({ onNavigate, onLogout, currentPage }) => {
    const { hasPermission, typeUser } = usePermissions();
    const [alertes, setAlertes] = useState([]);
    const [allExpenses, setAllExpenses] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [filterMode, setFilterMode] = useState('month');
    const [filterStartDate, setFilterStartDate] = useState(formatLocalDateInput());
    const [filterEndDate, setFilterEndDate] = useState(formatLocalDateInput());
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (typeUser && typeUser !== "admin" && typeUser !== "moderateur") {
            onNavigate("profile");
        }
    }, [onNavigate, typeUser]);

    const fetchData = async (mode = filterMode, startDate = filterStartDate, endDate = filterEndDate) => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const dateRange = mergeDateRange(mode, startDate, endDate);
            const [alertesRes, expensesRes] = await Promise.all([
                axios.get(`${API_URL}/vehicules/alertes`, {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                axios.get(`${API_URL}/depenses`, {
                    headers: { Authorization: `Bearer ${token}` },
                    params: {
                        entiteType: 'Vehicule',
                        startDate: dateRange.startDate,
                        endDate: dateRange.endDate
                    }
                })
            ]);

            setAlertes(alertesRes.data);
            setAllExpenses(expensesRes.data);
            setExpenses(expensesRes.data.slice(0, 10));
        } catch (error) {
            console.error('Erreur chargement dashboard véhicules:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(filterMode, filterStartDate, filterEndDate);
    }, [filterMode, filterStartDate, filterEndDate]);

    useEffect(() => {
        const presetRange = getPresetRangeForMode(filterMode);
        setFilterStartDate(presetRange.startDate.slice(0, 10));
        setFilterEndDate(presetRange.endDate.slice(0, 10));
    }, [filterMode]);

    const clearDateFilter = () => {
        const presetRange = getPresetRangeForMode(filterMode);
        setFilterStartDate(presetRange.startDate.slice(0, 10));
        setFilterEndDate(presetRange.endDate.slice(0, 10));
    };
    // ✨ Animations anime.js
    useEffect(() => {
        if (loading || typeof window.anime !== 'function') return;

        setTimeout(() => {
            window.anime({
                targets: '.dashboard-header',
                opacity: [0, 1],
                translateY: [-30, 0],
                duration: 800,
                easing: 'easeOutExpo'
            });

            window.anime({
                targets: '.alerts-section-premium',
                opacity: [0, 1],
                translateY: [20, 0],
                duration: 800,
                easing: 'easeOutQuad',
                delay: 200
            });

            window.anime({
                targets: '.chart-card',
                opacity: [0, 1],
                scale: [0.95, 1],
                duration: 800,
                easing: 'easeOutBack',
                delay: window.anime.stagger(150, {start: 400})
            });

            window.anime({
                targets: '.recent-expenses-section',
                opacity: [0, 1],
                translateY: [30, 0],
                duration: 800,
                easing: 'easeOutQuad',
                delay: 700
            });
        }, 100);
    }, [loading]);

    if (loading) {
        return (
            <div className="dashboard-container">
                <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />
                <div className="dashboard-content">
                    <LoadingSpinner message="Analyse de la flotte..." />
                </div>
            </div>
        );
    }

    const buildRepartition = (items, getKey) => items.reduce((acc, item) => {
        const key = getKey(item);
        const amount = Number(item.montant) || 0;
        const existingItem = acc.find(entry => entry.name === key);

        if (existingItem) {
            existingItem.value += amount;
        } else {
            acc.push({ name: key, value: amount });
        }

        return acc;
    }, []);

    // Préparation des données pour Recharts à partir du type lisible de la dépense
    const repartitionData = allExpenses.reduce((acc, expense) => {
        const typeName = expense.type?.trim() || 'Autre';
        const amount = Number(expense.montant) || 0;
        const existingItem = acc.find(item => item.name === typeName);

        if (existingItem) {
            existingItem.value += amount;
        } else {
            acc.push({ name: typeName, value: amount });
        }

        return acc;
    }, []);

    const repartitionVehiculesData = buildRepartition(allExpenses, (expense) => {
        return expense.entiteId?.matricule?.trim() || expense.entiteId?.marque?.trim() || 'Véhicule inconnu';
    });

    const totalDepenses = repartitionData.reduce((acc, curr) => acc + curr.value, 0);

    return (
        <div className="dashboard-container">
            <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />

            <div className="dashboard-content">
                <div className="dashboard-header">
                    <div>
                        <h1>Gestion de la Flotte</h1>
                        <p>Dépenses, maintenance et alertes administratives</p>
                    </div>
                    <button className="refresh-btn" onClick={() => fetchData(filterMode, filterStartDate, filterEndDate)}>Actualiser</button>
                </div>

                <div className="dashboard-filters">
                    <div className="filter-group">
                        <label htmlFor="vehicle-filter-mode">Période</label>
                        <select
                            id="vehicle-filter-mode"
                            value={filterMode}
                            onChange={(e) => setFilterMode(e.target.value)}
                        >
                            <option value="day">Jour</option>
                            <option value="week">Semaine</option>
                            <option value="month">Mois</option>
                            <option value="year">Année</option>
                        </select>
                    </div>
                    <div className="filter-group">
                        <label htmlFor="vehicle-filter-start">Date début</label>
                        <input
                            id="vehicle-filter-start"
                            type="date"
                            value={filterStartDate}
                            onChange={(e) => setFilterStartDate(e.target.value)}
                        />
                    </div>
                    <div className="filter-group">
                        <label htmlFor="vehicle-filter-end">Date fin</label>
                        <input
                            id="vehicle-filter-end"
                            type="date"
                            value={filterEndDate}
                            onChange={(e) => setFilterEndDate(e.target.value)}
                        />
                    </div>
                    <div className="filter-summary">
                        <span>Statistiques filtrées sur la plage sélectionnée.</span>
                        <strong>{totalDepenses.toFixed(2)} DT</strong>
                        <button type="button" className="filter-clear-btn" onClick={clearDateFilter} aria-label="Effacer le filtre de dates">
                            Effacer le filtre
                        </button>
                    </div>
                </div>

                {/* Alertes Administratives */}
                {alertes.length > 0 && (
                    <div className="alerts-section-premium">
                        <h2 className="section-title"><span className="icon">⚠️</span> Alertes d'expiration</h2>
                        <div className="alerts-grid">
                            {alertes.map(veh => (
                                <div key={veh._id} className="alert-card-premium">
                                    <div className="alert-header">
                                        <span className="veh-marque">{veh.marque}</span>
                                        <span className="veh-matricule">{veh.matricule}</span>
                                    </div>
                                    <div className="alert-body">
                                        {veh.alertes.map((alt, idx) => (
                                            <div key={idx} className={`alert-item ${alt.statut}`}>
                                                <span className="doc-label">{alt.doc}</span>
                                                <span className="doc-statut">
                                                    {alt.statut === 'expire' ? 'Expiré' : 'Expire bientôt'}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                    {veh.equipe && (
                                        <div className="alert-footer">
                                            Équipe: {veh.equipe.chefEquipe?.nom} {veh.equipe.chefEquipe?.prenom}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="charts-grid">
                    {/* Graphique de répartition des dépenses */}
                    <div className="chart-card">
                        <h3>Répartition des Dépenses par Type</h3>
                        {repartitionData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={350}>
                                <PieChart>
                                    <Pie
                                        data={repartitionData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={true}
                                        label={({ name, value }) => `${name}: ${value.toFixed(2)} DT`}
                                        outerRadius={100}
                                        dataKey="value"
                                    >
                                        {repartitionData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={Object.values(COLORS)[index % Object.values(COLORS).length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="no-data-message">
                                <p>Aucune dépense enregistrée pour le moment.</p>
                            </div>
                        )}
                    </div>

                    {/* Graphique de répartition des dépenses par véhicule */}
                    <div className="chart-card">
                        <h3>Répartition des Dépenses par Véhicule</h3>
                        {repartitionVehiculesData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={350}>
                                <BarChart data={repartitionVehiculesData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" angle={-25} textAnchor="end" height={90} interval={0} />
                                    <YAxis />
                                    <Tooltip formatter={(value) => `${Number(value).toFixed(2)} DT`} />
                                    <Legend />
                                    <Bar dataKey="value" name="Dépenses" fill={COLORS.primaryBlue} radius={[8, 8, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="no-data-message">
                                <p>Aucune dépense enregistrée pour le moment.</p>
                            </div>
                        )}
                    </div>

                    {/* Résumé global */}
                    <div className="chart-card stats-summary-card">
                        <h3>Vue d'Ensemble Financière</h3>
                        <div className="big-stat-display">
                            <span className="label">Total Dépenses Flotte</span>
                            <span className="value">
                                {totalDepenses.toFixed(2)} <small>DT</small>
                            </span>
                        </div>
                        <div className="sub-stats-list">
                            {repartitionData.sort((a,b) => b.value - a.value).map((item, i) => (
                                <div key={i} className="sub-stat-item">
                                    <span className="dot" style={{backgroundColor: Object.values(COLORS)[i % Object.values(COLORS).length]}}></span>
                                    <span className="name">{item.name}</span>
                                    <span className="amount">{item.value.toFixed(2)} DT</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Liste des dernières dépenses */}
                <div className="recent-expenses-section">
                    <h2 className="section-title">Dernières Dépenses Enregistrées</h2>
                    <div className="premium-table-wrapper">
                        <table className="premium-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Matricule</th>
                                    <th>Type</th>
                                    <th>Montant</th>
                                    <th>Responsable</th>
                                    <th>Note</th>
                                </tr>
                            </thead>
                            <tbody>
                                {expenses.map(exp => (
                                    <tr key={exp._id}>
                                        <td>{new Date(exp.datePaiement).toLocaleDateString('fr-FR')}</td>
                                        <td>
                                            <span className="badge-type">
                                                {exp.entiteId?.matricule || exp.entiteId?.marque || '-'}
                                            </span>
                                        </td>
                                        <td><span className="badge-type">{exp.type}</span></td>
                                        <td className="bold">{exp.montant.toFixed(2)} DT</td>
                                        <td>{exp.responsable?.nom} {exp.responsable?.prenom}</td>
                                        <td className="note-text">{exp.notes || '-'}</td>
                                    </tr>
                                ))}
                                {expenses.length === 0 && (
                                    <tr>
                                        <td colSpan="6" className="table-empty-message">
                                            <p>Aucune dépense trouvée pour cette période.</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardVehicule;
